/**
 * Workflow Canvas Component
 *
 * Visual canvas for building and arranging workflow steps with SVG connection lines.
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WorkflowStep } from '../../types/workflow';

/**
 * Canvas step representation
 */
export interface CanvasStep extends WorkflowStep {
  stepType: 'agent' | 'condition' | 'delay' | 'transform';
  icon: string;
}

/**
 * Connection line data
 */
interface ConnectionLine {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  type: 'sequential' | 'parallel' | 'conditional';
}

/**
 * Props for the WorkflowCanvas component
 */
interface WorkflowCanvasProps {
  steps: CanvasStep[];
  selectedStepId: string | null;
  onSelectStep: (stepId: string | null) => void;
  onRemoveStep: (stepId: string) => void;
  executionMode: string;
}

/**
 * Sortable step card on the canvas
 */
interface SortableStepCardProps {
  step: CanvasStep;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  executionMode: string;
  isLast: boolean;
  onPositionChange: (id: string, rect: DOMRect | null) => void;
}

function SortableStepCard({
  step,
  index,
  isSelected,
  onSelect,
  onRemove,
  isLast,
  onPositionChange,
}: SortableStepCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  // Report position changes
  useEffect(() => {
    const updatePosition = () => {
      if (cardRef.current) {
        onPositionChange(step.id, cardRef.current.getBoundingClientRect());
      }
    };
    updatePosition();

    // Update on resize
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [step.id, onPositionChange, transform]);

  // Combine refs
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [setNodeRef]);

  return (
    <div className="flex flex-col items-center" data-step-id={step.id}>
      {/* Step Card */}
      <div
        ref={combinedRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className={`
          w-[26rem] max-w-[90vw] p-4 bg-white border-2 rounded-lg shadow-sm cursor-grab
          transition-all hover:shadow-md step-card
          ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'}
          ${isDragging ? 'shadow-lg' : ''}
        `}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center text-xl">
              {step.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{step.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {step.stepType === 'agent' ? step.agentName : step.stepType}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded">
              #{index + 1}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="p-1 hover:bg-red-100 rounded text-red-500 transition-colors"
              title="Remove step"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Spacer for connection lines */}
      {!isLast && <div className="h-16" />}
    </div>
  );
}

/**
 * SVG Connection Lines Component
 */
interface SVGConnectionLinesProps {
  lines: ConnectionLine[];
  executionMode: string;
}

function SVGConnectionLines({ lines, executionMode }: SVGConnectionLinesProps) {
  // Get line color based on execution mode
  const getLineColor = (type: string) => {
    switch (type) {
      case 'sequential':
        return '#3b82f6'; // Blue
      case 'parallel':
        return '#22c55e'; // Green
      case 'conditional':
        return '#f59e0b'; // Amber
      default:
        return '#94a3b8'; // Slate
    }
  };

  // Get line style based on execution mode
  const getLineStyle = (type: string) => {
    if (type === 'conditional') {
      return '5,5'; // Dashed for conditional
    }
    return 'none'; // Solid for others
  };

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ overflow: 'visible' }}
    >
      <defs>
        {/* Arrow markers for different execution modes */}
        <marker
          id="arrowhead-sequential"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
        </marker>
        <marker
          id="arrowhead-parallel"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="#22c55e" />
        </marker>
        <marker
          id="arrowhead-conditional"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="#f59e0b" />
        </marker>

        {/* Glow filter for lines */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {lines.map((line) => {
        const color = getLineColor(line.type);
        const dashArray = getLineStyle(line.type);
        const markerId = `arrowhead-${line.type}`;

        // Calculate control points for a smooth curve
        const midY = (line.startY + line.endY) / 2;

        return (
          <g key={line.id}>
            {/* Shadow/glow line */}
            <path
              d={`M ${line.startX} ${line.startY}
                  C ${line.startX} ${midY},
                    ${line.endX} ${midY},
                    ${line.endX} ${line.endY}`}
              fill="none"
              stroke={color}
              strokeWidth="4"
              strokeOpacity="0.2"
              strokeLinecap="round"
            />
            {/* Main line */}
            <path
              d={`M ${line.startX} ${line.startY}
                  C ${line.startX} ${midY},
                    ${line.endX} ${midY},
                    ${line.endX} ${line.endY}`}
              fill="none"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={dashArray}
              markerEnd={`url(#${markerId})`}
            />
            {/* Execution mode indicator */}
            {executionMode === 'PARALLEL' && (
              <g transform={`translate(${line.startX + 10}, ${midY - 8})`}>
                <rect
                  x="-12"
                  y="-8"
                  width="24"
                  height="16"
                  rx="4"
                  fill="white"
                  stroke={color}
                  strokeWidth="1"
                />
                <text
                  x="0"
                  y="4"
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="bold"
                  fill={color}
                >
                  ||
                </text>
              </g>
            )}
            {executionMode === 'CONDITIONAL' && (
              <g transform={`translate(${line.startX + 10}, ${midY - 8})`}>
                <rect
                  x="-12"
                  y="-8"
                  width="24"
                  height="16"
                  rx="4"
                  fill="white"
                  stroke={color}
                  strokeWidth="1"
                />
                <text
                  x="0"
                  y="4"
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="bold"
                  fill={color}
                >
                  ?
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Workflow Canvas Component
 */
export function WorkflowCanvas({
  steps,
  selectedStepId,
  onSelectStep,
  onRemoveStep,
  executionMode,
}: WorkflowCanvasProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'workflow-canvas',
  });

  const canvasRef = useRef<HTMLDivElement>(null);
  const startNodeRef = useRef<HTMLDivElement>(null);
  const endNodeRef = useRef<HTMLDivElement>(null);
  const [stepPositions, setStepPositions] = useState<Map<string, DOMRect>>(new Map());
  const [connectionLines, setConnectionLines] = useState<ConnectionLine[]>([]);

  // Handle step position changes
  const handlePositionChange = useCallback((id: string, rect: DOMRect | null) => {
    setStepPositions((prev) => {
      const newMap = new Map(prev);
      if (rect) {
        newMap.set(id, rect);
      } else {
        newMap.delete(id);
      }
      return newMap;
    });
  }, []);

  // Calculate connection lines when positions change
  useEffect(() => {
    if (!canvasRef.current || steps.length === 0) {
      setConnectionLines([]);
      return;
    }

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newLines: ConnectionLine[] = [];

    // Get execution type for lines
    const lineType = executionMode === 'SEQUENTIAL'
      ? 'sequential'
      : executionMode === 'PARALLEL'
        ? 'parallel'
        : 'conditional';

    // Line from start node to first step
    if (startNodeRef.current && steps.length > 0) {
      const startRect = startNodeRef.current.getBoundingClientRect();
      const firstStepRect = stepPositions.get(steps[0].id);

      if (firstStepRect) {
        newLines.push({
          id: 'start-to-first',
          startX: startRect.left + startRect.width / 2 - canvasRect.left,
          startY: startRect.bottom - canvasRect.top,
          endX: firstStepRect.left + firstStepRect.width / 2 - canvasRect.left,
          endY: firstStepRect.top - canvasRect.top,
          type: lineType,
        });
      }
    }

    // Lines between steps
    for (let i = 0; i < steps.length - 1; i++) {
      const currentStepRect = stepPositions.get(steps[i].id);
      const nextStepRect = stepPositions.get(steps[i + 1].id);

      if (currentStepRect && nextStepRect) {
        newLines.push({
          id: `step-${i}-to-${i + 1}`,
          startX: currentStepRect.left + currentStepRect.width / 2 - canvasRect.left,
          startY: currentStepRect.bottom - canvasRect.top,
          endX: nextStepRect.left + nextStepRect.width / 2 - canvasRect.left,
          endY: nextStepRect.top - canvasRect.top,
          type: lineType,
        });
      }
    }

    // Line from last step to end node
    if (endNodeRef.current && steps.length > 0) {
      const endRect = endNodeRef.current.getBoundingClientRect();
      const lastStepRect = stepPositions.get(steps[steps.length - 1].id);

      if (lastStepRect) {
        newLines.push({
          id: 'last-to-end',
          startX: lastStepRect.left + lastStepRect.width / 2 - canvasRect.left,
          startY: lastStepRect.bottom - canvasRect.top,
          endX: endRect.left + endRect.width / 2 - canvasRect.left,
          endY: endRect.top - canvasRect.top,
          type: lineType,
        });
      }
    }

    setConnectionLines(newLines);
  }, [steps, stepPositions, executionMode]);

  // Update positions on scroll
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleScroll = () => {
      // Force recalculation by triggering a state update
      setStepPositions((prev) => new Map(prev));
    };

    canvas.addEventListener('scroll', handleScroll);
    return () => canvas.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        (canvasRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      className={`
        flex-1 bg-slate-100 overflow-auto p-8 2xl:pr-[20%] relative
        ${isOver ? 'bg-blue-50' : ''}
      `}
      onClick={() => onSelectStep(null)}
    >
      {/* SVG Connection Lines Layer */}
      <SVGConnectionLines lines={connectionLines} executionMode={executionMode} />

      {/* Canvas Header */}
      <div className="text-center mb-8 relative z-10">
        <h3 className="text-lg font-semibold text-slate-700">Workflow Steps</h3>
        <p className="text-sm text-muted-foreground">
          {steps.length === 0
            ? 'Drag steps from the toolbox to start building'
            : `${steps.length} step${steps.length !== 1 ? 's' : ''} in workflow`}
        </p>
        {/* Execution mode legend */}
        {steps.length > 0 && (
          <div className="flex items-center justify-center gap-4 mt-2">
            <div className="flex items-center gap-1 text-xs">
              <div className="w-4 h-0.5 bg-blue-500 rounded" />
              <span className="text-muted-foreground">Sequential</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <div className="w-4 h-0.5 bg-green-500 rounded" />
              <span className="text-muted-foreground">Parallel</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <div className="w-4 h-0.5 bg-amber-500 rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #f59e0b, #f59e0b 3px, transparent 3px, transparent 6px)' }} />
              <span className="text-muted-foreground">Conditional</span>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {steps.length === 0 && (
        <div
          className={`
            mx-auto max-w-md p-12 border-2 border-dashed rounded-xl
            text-center transition-colors relative z-10
            ${isOver ? 'border-primary bg-primary/5' : 'border-slate-300'}
          `}
        >
          <div className="text-5xl mb-4">🎯</div>
          <h4 className="text-lg font-medium mb-2">Drop Steps Here</h4>
          <p className="text-sm text-muted-foreground">
            Drag agents or flow control steps from the toolbox to add them to
            your workflow
          </p>
        </div>
      )}

      {/* Steps List */}
      {steps.length > 0 && (
        <div className="flex flex-col items-center relative z-10">
          {/* Start Node */}
          <div
            ref={startNodeRef}
            className="w-16 h-16 bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center text-2xl shadow-md"
          >
            ▶️
          </div>
          <div className="text-xs text-muted-foreground mt-1 mb-1 font-medium">Start</div>
          <div className="h-8" /> {/* Spacer for start line */}

          {/* Sortable Steps */}
          <SortableContext
            items={steps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {steps.map((step, index) => (
              <SortableStepCard
                key={step.id}
                step={step}
                index={index}
                isSelected={selectedStepId === step.id}
                onSelect={() => onSelectStep(step.id)}
                onRemove={() => onRemoveStep(step.id)}
                executionMode={executionMode}
                isLast={index === steps.length - 1}
                onPositionChange={handlePositionChange}
              />
            ))}
          </SortableContext>

          {/* End Node */}
          <div className="h-8" /> {/* Spacer for end line */}
          <div className="text-xs text-muted-foreground mb-1 font-medium">End</div>
          <div
            ref={endNodeRef}
            className="w-16 h-16 bg-red-100 border-2 border-red-500 rounded-full flex items-center justify-center text-2xl shadow-md"
          >
            ⏹️
          </div>
        </div>
      )}

      {/* Drop indicator overlay */}
      {isOver && steps.length > 0 && (
        <div className="absolute inset-0 bg-primary/5 pointer-events-none z-0" />
      )}
    </div>
  );
}

export default WorkflowCanvas;
